import type {
  ColumnPinningPosition,
  ColumnPinningState,
} from '@tanstack/react-table';
import { defaultRangeExtractor, type Range } from '@tanstack/react-virtual';

/**
 * Adapts the visible range indexes to include pinned columns.
 *
 * This function is passed to the `rangeExtractor` of TanStack Virtualization.
 * It modifies the visible range indexes to ensure that pinned columns are preserved
 * within the range of visible columns.
 *
 * @param range - the array of indexes representing the initial visible range.
 * @param pinnedLeftIndexes - the list of indexes pinned to the left.
 * @param pinnedRightIndexes - the list of indexes pinned to the right.
 * @returns An array of indexes with pinned columns included in the visible range.
 */
export function addPinnedColumnsToVisibleRange(
  range: Range,
  pinnedLeftIndexes: number[],
  pinnedRightIndexes: number[],
): number[] {
  const defaultRange = defaultRangeExtractor(range);

  if (pinnedLeftIndexes.length === 0 && pinnedRightIndexes.length === 0) {
    return defaultRange;
  }

  return [
    ...new Set([...pinnedLeftIndexes, ...defaultRange, ...pinnedRightIndexes]),
  ];
}

/**
 * Maps a logical pinning position to the physical side it renders on.
 *
 * TanStack table uses logical `start`/`end` terminology, but since the DataTable is
 * only ever LTR, we use `data-pinned-position` to describe a physical screen edge.
 *
 * @param position - the logical pinning position.
 * @returns `left` or `right` respectively, or `undefined` when the column is not pinned.
 */
export function toPhysicalPinnedPosition(
  position: ColumnPinningPosition,
): 'left' | 'right' | false {
  if (position === 'start') {
    return 'left';
  }
  if (position === 'end') {
    return 'right';
  }
  return false;
}

/**
 * @param idsToPin - the leaf columns to pin.
 * @param oldState - the previous column pinning state.
 * @param position - the position to pin the columns to.
 * @returns a new column pinning state with the given columns start/end pinned or unpinned.
 */
export function pinColumns(
  idsToPin: string[],
  oldState: ColumnPinningState,
  position: ColumnPinningPosition,
): ColumnPinningState {
  const { start: prevStart = [], end: prevEnd = [] } = oldState;

  const start =
    position === 'start'
      ? // add column(s) to start pinned
        [...prevStart, ...idsToPin]
      : // remove column(s) from start pinned
        prevStart.filter((colId) => !idsToPin.includes(colId));

  const end =
    position === 'end'
      ? // add column(s) to end pinned
        [...idsToPin, ...prevEnd]
      : // remove column(s) from end pinned
        prevEnd.filter((colId) => !idsToPin.includes(colId));
  return { start, end };
}

/**
 * Determines whether a given column is pinned based on the given pinning state.
 *
 * @param columnId - The ID of the column to check.
 * @param columnsPinned - A pinning state object that is used to determine the column's pinning state.
 * @returns `true` if the column is pinned (either to the start or end) in the provided state.
 *
 */
export function getIsColumnPinned(
  columnId: string,
  columnsPinned: ColumnPinningState,
): boolean {
  return (
    (columnsPinned.start?.includes(columnId) ||
      columnsPinned.end?.includes(columnId)) ??
    false
  );
}
